import {
  styled,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from '@mui/material';

export const FormContainer = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
});

export const ScrollableContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: '12px',
  scrollbarWidth: 'thin',
  scrollbarColor: '#c1c1c1 #f1f1f1',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
  },
});

export const Wrapper = styled(Box)({
  display: 'flex',
  gap: '16px',
  marginBottom: 8,
});

export const Column = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '25px',
  flex: 1,
  minWidth: '200px',
});

export const InnerColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const SmallColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const DeliveryNoteColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '-1px',
});

export const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'hasValue' && prop !== 'hasError',
})<{ hasValue?: boolean; hasError?: boolean }>(({ hasValue, hasError }) => ({
  width: '100%',
  maxWidth: '294px',
  height: '40px',
  backgroundColor: '#FCFEFF',
  borderRadius: '8px',
  '& .MuiSelect-select': {
    padding: '10px 16px',
    fontFamily: 'Outfit',
    fontSize: '14px',
    fontWeight: 400,
    color: hasValue ? '#0B2330' : '#75838A',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? '#FF0000 !important' : '#D9F1FF',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0F52BA',
  },
  '& .MuiSvgIcon-root': {
    color: '#555555',
  },
}));

export const StyledAutocomplete = styled(Autocomplete)({
  width: '100%',
  maxWidth: '294px',
  '& .MuiAutocomplete-listbox': {
    maxHeight: '350px',
    overflow: 'auto',
  },
}) as typeof Autocomplete;

export const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasValue' && prop !== 'hasError',
})<{ hasValue?: boolean; hasError?: boolean }>(({ hasValue, hasError }) => ({
  '& .MuiOutlinedInput-root': {
    height: '40px',
    fontSize: '14px',
    fontFamily: 'Outfit',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderColor: hasError ? '#FF0000' : hasValue ? '#0099FF' : '#D9F1FF',
    },
    '&:hover fieldset': {
      borderColor: hasError ? '#FF0000' : '#0099FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? '#FF0000' : '#0099FF',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F5F5F5',
    },
  },
  '& .MuiInputBase-input': {
    padding: '0 14px !important',
    fontSize: '14px',
    fontFamily: 'Outfit',
    fontWeight: 400,
    color: hasValue ? '#0B2330' : '#75838A',
    '&::placeholder': {
      color: '#75838A',
      opacity: 1,
    },
  },
}));

export const AddressCardEmpty = styled(Box)({
  width: '100%',
  maxWidth: '278px',
  minWidth: '250px',
  height: '136px',
  backgroundColor: '#FAFAFA',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  marginTop: '14px',
});

export const AddressIcon = styled('i')({
  fontSize: '24px',
  color: '#003572',
});

export const SelectAddressText = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: '#75838A',
});

export const AddressCardFilled = styled(Box)({
  width: '100%',
  maxWidth: '278px',
  minWidth: '250px',
  height: '155px',
  backgroundColor: '#F1F7FF',
  border: '1px solid #D9F1FF',
  borderRadius: '8px',
  padding: 8,
  boxSizing: 'border-box',
  overflow: 'hidden',
});

export const TitleTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: '#555555',
});

export const SectionTitle = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontSize: '14px',
  color: '#555555',
  marginBottom: 8,
});

export const AddressText = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '12px',
  color: '#0B2330',
  lineHeight: 1.4,
});

export const AddressContactRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 8,
});

export const AddressContactText = styled(Typography)({
  fontSize: '14px',
  color: '#003572',
});

export const HiddenTitle = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: '#555555',
  visibility: 'hidden',
});

export const Height40Box = styled(Box)({
  height: '40px',
});

export const OptionalSpan = styled('span')({
  fontStyle: 'italic',
  fontSize: '14px',
  color: '#75838A',
});

export const FieldContainer = styled(Box)({
  position: 'relative',
  display: 'inline-block',
});

export const ErrorAbsolute = styled(Typography)({
  fontFamily: 'Outfit',
  position: 'absolute',
  top: '44px',
  left: 0,
  color: '#FF0000',
  fontSize: '14px',
  zIndex: 10,
});

export const Mt1Box = styled(Box)({
  marginTop: 8,
});

export const NextButton = styled(Button)({
  width: '82px',
  height: '40px',
  backgroundColor: '#0F52BA',
  border: '1px solid #0D4AA9',
  borderRadius: '8px',
  boxShadow: '0px 2px 4px #E2E3E1',
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontSize: '16px',
  color: '#FFFFFF',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#0D4AA9',
  },
});

export const NextButtonContainer = styled(Box)({
  flexShrink: 0,
  paddingTop: 16,
  backgroundColor: '#FFFFFF',
  display: 'flex',
  justifyContent: 'flex-end',
});
